import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  CompleteOrderService,
  ICheckoutComEvents,
  CHECKOUT_COM_CARD_FORM_STATES,
  checkoutcomCardFormMachine,
} from '../stateMachines';
import { useMachine } from '@monofront/xstate-v4/react';
import { StateFrom, Sender, InterpreterFrom } from '@monofront/xstate-v4';
import { v4 as uuid } from 'uuid';
interface ICheckoutComMachineContext {
  state: StateFrom<typeof checkoutcomCardFormMachine>;
  send: Sender<ICheckoutComEvents>;
  interpreter: InterpreterFrom<typeof checkoutcomCardFormMachine> | null;
  registerCompleteOrder: (completeOrder: CompleteOrderService) => void;
}

const CheckoutComMachineContext = createContext<ICheckoutComMachineContext>({
  state: checkoutcomCardFormMachine.initialState,
  send: () => undefined,
  interpreter: null,
  registerCompleteOrder: () => undefined,
});

const CheckoutComMachineProvider = ({
  children,
  sendMessageToMobile,
  onFlowPaymentCompleted,
}: {
  children: React.ReactNode;
  sendMessageToMobile: (...args: any[]) => void;
  onFlowPaymentCompleted?: (paymentId: string) => void;
}) => {
  const generatedCheckout3dsId = useMemo(() => uuid().toString(), []);
  const onFlowPaymentCompletedRef = useRef(onFlowPaymentCompleted);

  // This provider sits above CheckoutPaymentProvider, so `useCompleteOrder` is
  // not reachable here. The payment CTA currently mounted (PaymentActions only
  // ever renders one) registers its own post-3DS routine instead.
  const completeOrderRef = useRef<CompleteOrderService | null>(null);

  const registerCompleteOrder = useCallback(
    (completeOrder: CompleteOrderService) => {
      completeOrderRef.current = completeOrder;
    },
    [],
  );

  onFlowPaymentCompletedRef.current = onFlowPaymentCompleted;

  const machine = useMemo(
    () =>
      checkoutcomCardFormMachine.withConfig({
        actions: {
          create3dsToken: () => {
            sendMessageToMobile('3dsStarted', { generatedCheckout3dsId });
          },
          remove3dsToken: () => {
            sendMessageToMobile('3dsStopped');
          },
        },
        services: {
          // Rejecting routes the machine to onError, which returns it to INITIAL.
          // The CTA has already shown the customer why completion failed.
          completeOrder: async (context) => {
            if (!completeOrderRef.current) {
              throw new Error('No completeOrder routine registered.');
            }

            if (!(await completeOrderRef.current(context))) {
              throw new Error('Complete order reported a failure.');
            }
          },
        },
      }),
    [],
  );

  const [state, send, interpreter] = useMachine(machine);

  useEffect(() => {
    // Handles case where 3DS is not displayed for customer.
    // We need to notify the parent component that the flow is completed so
    // it can proceed to the next step of the checkout
    if (
      state.value !== CHECKOUT_COM_CARD_FORM_STATES.FLOW_PAYMENT_COMPLETED ||
      !state.context.flowPaymentId
    ) {
      return;
    }
    onFlowPaymentCompletedRef.current?.(state.context.flowPaymentId);
  }, [state.value, state.context.flowPaymentId]);

  return (
    <CheckoutComMachineContext.Provider
      value={{ state, send, interpreter, registerCompleteOrder }}
    >
      {children}
    </CheckoutComMachineContext.Provider>
  );
};

const useCheckoutComMachine = () => useContext(CheckoutComMachineContext);

export { CheckoutComMachineProvider, useCheckoutComMachine };
